import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'negociacoes.activeLoteId';
const CUSTOM_EVENT = 'negociacoes.activeLote.change';

function readFromStorage(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeToStorage(loteId: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (loteId) {
      window.localStorage.setItem(STORAGE_KEY, loteId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: loteId }));
  } catch {
    return;
  }
}

export function useActiveLoteProcessing() {
  const [activeLoteId, setActiveLoteIdState] = useState<string | null>(() => readFromStorage());

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setActiveLoteIdState(event.newValue);
      }
    };

    const handleCustom = (event: Event) => {
      const detail = (event as CustomEvent<string | null>).detail ?? null;
      setActiveLoteIdState(detail);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(CUSTOM_EVENT, handleCustom as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(CUSTOM_EVENT, handleCustom as EventListener);
    };
  }, []);

  const setActiveLoteId = useCallback((loteId: string | null) => {
    writeToStorage(loteId);
    setActiveLoteIdState(loteId);
  }, []);

  const clearActiveLoteId = useCallback(() => {
    writeToStorage(null);
    setActiveLoteIdState(null);
  }, []);

  return { activeLoteId, setActiveLoteId, clearActiveLoteId };
}
